/**
 * Deep-merge utility for responsivePosition.breakpoints.
 *
 * Merge rules per breakpoint sub-property:
 *   style       → recursive deep-merge
 *   properties  → recursive deep-merge
 *   constraints → replace
 *   size        → replace
 *   hidden      → replace
 *   position    → replace
 */

import type { BreakpointOverride, ResponsivePosition } from '@/types';
import { isRecord } from "./index";

/**
 * Recursively deep-merge two plain objects. Skips null/undefined source values.
 * Arrays and primitives in source replace target entirely.
 */
export function deepMergeObjects(
  target: Record<string, unknown>,
  source: Record<string, unknown>,
): Record<string, unknown> {
  const result = { ...target };

  for (const key of Object.keys(source)) {
    const sourceValue = source[key];
    const targetValue = target[key];

    // Skip null/undefined — don't overwrite with nothing
    if (sourceValue === undefined || sourceValue === null) {
      continue;
    }

    // Both are plain objects (not arrays) → recurse
    if (isRecord(sourceValue) && isRecord(targetValue)) {
      result[key] = deepMergeObjects(targetValue, sourceValue);
    } else {
      // Primitives, arrays, or type mismatch → source wins
      result[key] = sourceValue;
    }
  }

  return result;
}

type BreakpointMap = ResponsivePosition['breakpoints'];
const BREAKPOINT_KEYS = ['mobile', 'tablet', 'desktop'] as const;

/**
 * Deep-merge breakpoint maps. For each breakpoint key present in `incoming`,
 * style and properties are recursively deep-merged while constraints, size,
 * hidden, and position are replaced. Breakpoints not mentioned in `incoming`
 * are preserved from `existing`.
 */
export function deepMergeBreakpoints(
  existing: BreakpointMap | undefined,
  incoming: BreakpointMap | undefined,
): BreakpointMap | undefined {
  if (!incoming) return existing;
  if (!existing) return incoming;

  const merged: BreakpointMap = { ...existing };

  for (const breakpointKey of BREAKPOINT_KEYS) {
    const existingBp = existing[breakpointKey] || {};
    const incomingBp = incoming[breakpointKey];
    if (!incomingBp) continue;

    const mergedBp: BreakpointOverride = { ...existingBp };

    // style: recursive deep-merge
    if (incomingBp.style !== undefined) {
      mergedBp.style = existingBp.style
        ? deepMergeObjects(existingBp.style, incomingBp.style)
        : { ...incomingBp.style };
    }

    // properties: recursive deep-merge
    if (incomingBp.properties !== undefined) {
      mergedBp.properties = existingBp.properties
        ? deepMergeObjects(existingBp.properties, incomingBp.properties)
        : { ...incomingBp.properties };
    }

    // constraints: replace
    if (incomingBp.constraints !== undefined) {
      mergedBp.constraints = incomingBp.constraints;
    }

    // size: replace
    if (incomingBp.size !== undefined) {
      mergedBp.size = incomingBp.size;
    }

    // hidden: replace
    if (incomingBp.hidden !== undefined) {
      mergedBp.hidden = incomingBp.hidden;
    }

    // position: replace
    if (incomingBp.position !== undefined) {
      mergedBp.position = incomingBp.position;
    }

    merged[breakpointKey] = mergedBp;
  }

  return merged;
}
